import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PROGRAMS } from "@/data/programs";
import TrustedPartners from "@/components/TrustedPartners";
import {
  BookOpen, Rocket, ArrowRight, Loader2, Check, GraduationCap,
  Hammer, FlaskConical, Presentation, Home as HomeIcon,
} from "lucide-react";

const PATHWAY_SLUGS = ["flight-club", "cinemagic-kids", "beats", "design-district", "krypto-kids"];
const PATHWAYS = PATHWAY_SLUGS.map((s) => PROGRAMS.find((p) => p.slug === s)).filter(Boolean);

const STEPS = [
  { icon: BookOpen, title: "Create", text: "Students start with their interests and turn ideas into real projects." },
  { icon: Hammer, title: "Build", text: "Hands-on making with real tools, technology, and materials." },
  { icon: FlaskConical, title: "Test & Improve", text: "They try, refine, and problem-solve their way to something better." },
  { icon: Presentation, title: "Perform", text: "Students present and demonstrate what they've mastered." },
];

const SKILLS = ["Reading", "Writing", "Vocabulary", "Math", "Science", "Communication", "Critical Thinking"];

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:ring-2 focus:ring-accent/40 focus:border-accent";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function HomeschoolForm() {
  const [form, setForm] = useState({ parent_name: "", email: "", phone: "", child_age: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/program-inquiries", { ...form, program: "Homeschool Program" });
      setDone(true);
      toast.success("Thank you! We'll reach out soon.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="homeschool-success" className="rounded-3xl border border-slate-100 bg-white p-10 text-center shadow-lg">
        <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-green-100 text-green-600"><Check className="h-8 w-8" /></span>
        <h3 className="mt-5 font-display text-2xl font-black text-brandnavy">Inquiry received!</h3>
        <p className="mt-2 text-slate-600">Thanks for your interest in LP Foundation Homeschool. Our team will be in touch shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-3xl border border-slate-100 bg-white p-6 sm:p-8 shadow-lg space-y-4" data-testid="homeschool-form">
      <div className="flex items-center gap-2">
        <HomeIcon className="h-5 w-5 text-accent" />
        <h3 className="font-display text-xl font-bold text-brandnavy">Request homeschool info</h3>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Parent / Guardian Name *"><input data-testid="homeschool-parent" required value={form.parent_name} onChange={update("parent_name")} className={inputCls} /></Field>
        <Field label="Email *"><input data-testid="homeschool-email" type="email" required value={form.email} onChange={update("email")} className={inputCls} /></Field>
        <Field label="Phone"><input data-testid="homeschool-phone" value={form.phone} onChange={update("phone")} className={inputCls} /></Field>
        <Field label="Student Age(s)"><input data-testid="homeschool-age" value={form.child_age} onChange={update("child_age")} className={inputCls} /></Field>
      </div>
      <Field label="Tell us about your homeschool goals *">
        <textarea data-testid="homeschool-message" required rows={4} value={form.message} onChange={update("message")} className={inputCls} />
      </Field>
      <button data-testid="homeschool-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent px-6 py-4 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-orange-400 disabled:opacity-60">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <GraduationCap className="h-5 w-5" />} Request Information
      </button>
    </form>
  );
}

export default function Homeschool() {
  return (
    <div data-testid="homeschool-page">
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-brandnavy">
        <div className="absolute -left-24 -top-24 h-96 w-96 rounded-full bg-accent/20 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-28">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold tracking-[0.2em] uppercase text-white">
            <HomeIcon className="h-3.5 w-3.5 text-accent" /> LP Foundation Homeschool
          </span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.05] max-w-3xl">
            Learning that turns into <span className="text-accent">doing.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
            LP Foundation Homeschool turns learning into a hands-on experience by connecting academics, literacy, and
            career exploration with students' interests. Through pathways in aviation, media, music, design, financial
            literacy, and technology, students build real-world skills while strengthening reading, writing, vocabulary,
            math, science, communication, and critical thinking.
          </p>
          <p className="mt-4 max-w-2xl text-lg font-semibold text-white">
            Students don't just learn — they create, build, test, improve, and perform.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <a href="#inquiry" data-testid="homeschool-hero-cta" className="inline-flex items-center gap-2 rounded-full bg-accent px-7 py-3.5 font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-orange-400">
              <GraduationCap className="h-5 w-5" /> Request Information
            </a>
            <Link to="/programs" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 font-semibold text-white transition-all duration-300 hover:bg-white/10">
              Explore the pathways <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <div className="max-w-2xl">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">How It Works</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-brandnavy">More than lessons — a learning cycle.</h2>
          <p className="mt-4 text-slate-600">Every pathway follows the same hands-on rhythm, so academics stick because students apply them to something real.</p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <div key={s.title} data-testid={`step-${i + 1}`} className="relative rounded-3xl border border-slate-100 bg-white p-7 shadow-sm">
              <span className="absolute right-6 top-6 font-display text-5xl font-black text-slate-100">{i + 1}</span>
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-orange-50 text-accent"><s.icon className="h-6 w-6" /></span>
              <h3 className="mt-5 font-display text-lg font-bold text-brandnavy">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* GALLERY */}
      <section className="bg-white py-20" data-testid="homeschool-gallery">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Homeschool in Action</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-brandnavy">Real students. Real projects.</h2>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {[
              { src: "/img/homeschool/hs2.webp", alt: "Students building a hands-on project" },
              { src: "/img/homeschool/drone.webp", alt: "Student learning to fly a drone outdoors" },
              { src: "/img/homeschool/hs1.webp", alt: "Students launching balloon rockets" },
              { src: "/img/homeschool/hs3.webp", alt: "Students building the SEAA landing pad challenge" },
              { src: "/img/homeschool/creditunion.webp", alt: "Students on a financial literacy field day" },
              { src: "/img/homeschool/hs4.webp", alt: "Student working through an activity" },
              { src: "/img/homeschool/hs5.webp", alt: "Students collaborating at the table" },
            ].map((p) => (
              <div key={p.src} className="overflow-hidden rounded-3xl aspect-[3/4]">
                <img src={p.src} alt={p.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEOS */}
      <section className="bg-slate-50 py-20" data-testid="homeschool-videos">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Watch & Learn</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-brandnavy">See our pathways in motion.</h2>
          </div>
          <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { id: "flight", src: "/videos/flightclub_web.mp4", poster: "/videos/flightclub_poster.jpg", label: "Aviation Pathway — Flight Club" },
              { id: "beats", src: "/videos/beats_web.mp4", poster: "/videos/beats_poster.jpg", label: "Music Pathway — Beats, Rhythms & Kids" },
              { id: "seaa", src: "/videos/seaa_web.mp4", poster: "/videos/seaa_poster.jpg", label: "SEAA Enrichment — Landing Pad Engineering Challenge" },
            ].map((v) => (
              <div key={v.id} className="flex flex-col items-center">
                <video
                  data-testid={`homeschool-video-${v.id}`}
                  controls
                  playsInline
                  preload="metadata"
                  poster={v.poster}
                  className="w-full max-w-sm rounded-3xl border border-slate-100 bg-black shadow-sm"
                >
                  <source src={v.src} type="video/mp4" />
                </video>
                <p className="mt-4 text-center text-sm font-semibold text-brandnavy">{v.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PATHWAYS */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Homeschool Pathways</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-brandnavy">Choose the pathways that spark your student.</h2>
            <p className="mt-4 text-slate-600">Aviation, media, music, design, financial literacy, and technology — each one blends core academics with real-world creation.</p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PATHWAYS.map((p) => (
              <div key={p.slug} data-testid={`homeschool-pathway-${p.slug}`} className="group overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl">
                <div className="h-44 overflow-hidden">
                  <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" style={{ filter: p.filter }} />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-lg font-bold text-brandnavy">{p.name}</h3>
                  <p className="mt-1 text-xs font-medium uppercase tracking-wide" style={{ color: p.color }}>{p.tag}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SKILLS */}
      <section className="bg-brandnavy py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Academics, Strengthened</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-white">Real projects reinforce real fundamentals.</h2>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            {SKILLS.map((s) => (
              <span key={s} className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <TrustedPartners
        eyebrow="Program Partners"
        title="Backed by trusted partners."
        subtitle="Our homeschool pathways are strengthened by partnerships with organizations invested in student success."
      />

      {/* INQUIRY */}
      <section id="inquiry" className="bg-slate-50 py-20 scroll-mt-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Homeschool Families</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-brandnavy">Bring hands-on learning home.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Tell us a little about your family and goals, and our team will share how LP Foundation Homeschool can fit
              your student's interests and schedule.
            </p>
            <ul className="mt-6 space-y-3">
              {["Interest-driven pathways", "Academics reinforced through projects", "Career exploration & real skills", "Create · Build · Test · Improve · Perform"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-slate-700">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-orange-100 text-accent"><Check className="h-3.5 w-3.5" /></span>{t}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <HomeschoolForm />
          </div>
        </div>
      </section>
    </div>
  );
}
